import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, type Observable, timeout } from 'rxjs';
import type { Invoice } from '../models/invoice';
import type { BulkActionResponse } from '../models/bulk-action-response';
import type { OcrResponse } from '../models/ocr/ocr-response';
import type { InvoiceDetails } from '../models/invoice-details';
import type { OcrSuggestions } from '../models/ocr/ocr-suggestion';
import type { Requisitioner } from '../models/requisitioner';
import type { ParentCompany } from '../models/parent-company';

@Injectable({
	providedIn: 'root'
})
export class EFilingHttpService {
	private httpClient = inject<HttpClient>(HttpClient);

	//point to the e-filing controller of the corresponding api url
	private baseUrl = environment.portalApi.uri + '/e-filing';

	//gets the data for the e-Filing view
	public getEFilingData(
		_parentCompany: string | null = null
	): Observable<Invoice[]> {
		if (_parentCompany) {
			return this.getSavedSearch(_parentCompany);
		}

		return this.httpClient
			.get<BulkActionResponse[]>(this.baseUrl + '/invoices/bulk')
			.pipe(
				map((responses) =>
					responses.flatMap((r) => r.data as Invoice[])
				)
			);
	}

	private getSavedSearch(parentCompany: string): Observable<Invoice[]> {
		return this.httpClient.get<Invoice[]>(this.baseUrl + '/invoices', {
			params: {
				parentCompany: parentCompany
			}
		});
	}

	public submitInvoices(
		invoices: Invoice[],
		destination: string | null = null
	): Observable<BulkActionResponse[]> {
		let params: Record<string, string> = {};

		if (destination && invoices.length === 1) {
			params = { destination };
		}

		return this.httpClient.post<BulkActionResponse[]>(
			this.baseUrl + '/invoices/submit/bulk',
			invoices,
			{ params }
		);
	}

	public getSuggestions(
		invoice: Invoice,
		refresh: boolean
	): Observable<OcrResponse> {
		const params: Record<string, string> = {
			parentCompany: invoice.parentCompany
		};

		if (refresh) {
			params['refresh'] = refresh.toString();
		}

		return this.httpClient.get<OcrResponse>(
			this.baseUrl + `/invoices/${invoice.internalId}/suggestions`,
			{ params }
		);
	}

	public getInvoiceDetails(
		internalId: number,
		parentCompany: string
	): Observable<InvoiceDetails> {
		return this.httpClient.get<InvoiceDetails>(
			`${this.baseUrl}/invoices/${internalId}`,
			{ params: { parentCompany } }
		);
	}

	public acceptSuggestions(
		invoiceId: number,
		parentCompany: string,
		suggestions: OcrSuggestions,
		updateOnly = false
	): Observable<Invoice> {
		const url = `${this.baseUrl}/invoices/${invoiceId}/suggestions`;
		const params: Record<string, string> = { parentCompany };
		if (updateOnly) params['updateOnly'] = 'true';

		return this.httpClient.post<Invoice>(
			url,
			{ ...suggestions },
			{ params }
		);
	}

	public getRequisitioners(): Observable<Record<string, Requisitioner[]>> {
		return this.httpClient.get<Record<string, Requisitioner[]>>(
			`${this.baseUrl}/requisitioners`
		);
	}

	public resetInvoice(
		invoiceId: number,
		parentCompany: string
	): Observable<Invoice> {
		return this.httpClient.delete<Invoice>(
			`${this.baseUrl}/invoices/${invoiceId}/reset`,
			{ params: { parentCompany: parentCompany } }
		);
	}

	public refreshInvoices(): Observable<boolean> {
		return this.httpClient
			.post<boolean>(`${this.baseUrl}/invoices/refresh`, {})
			.pipe(timeout(120_000));
	}

	public getParentCompanies(): Observable<ParentCompany[]> {
		return this.httpClient.get<ParentCompany[]>(
			`${this.baseUrl}/parentCompanies`
		);
	}

	public getFromTicketPayload(invoiceId: string): Observable<unknown> {
		const testBaseUrl = environment.portalApi.uri + '/test';
		return this.httpClient.get<unknown>(
			`${testBaseUrl}/invoices/${invoiceId}/mime-invoice-payload`
		);
	}

	public manualTicketFlip(
		ticketListItem: Record<string, unknown>
	): Observable<unknown> {
		const url =
			environment.portalApi.uri + '/test/invoices/flipTicketToInvoice';
		return this.httpClient.post(url, { ...ticketListItem });
	}
}
